import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { StableInput, StableTextarea } from '../components/StableInput';
import { 
  Building2, 
  Save, 
  Globe, 
  Phone, 
  Mail, 
  MapPin,
  Target,
  Calendar,
  Palette,
  User,
  Loader2,
  Upload,
  X,
  Image
} from 'lucide-react';

export default function MyOrganization() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const hasFetched = useRef(false);
  const [formData, setFormData] = useState({
    name: '',
    mission: '',
    vision: '',
    founded_year: '',
    location: '',
    website: '',
    tax_id: '',
    impact_stats: '',
    programs_overview: '',
    logo_url: '',
    primary_color: '#2F5233',
    secondary_color: '#E07A5F',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
  });

  // Auth guard - separate effect, no user dependency
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
    }
  }, [authLoading, navigate, user]);

  // Data fetch - runs once on mount
  useEffect(() => {
    if (authLoading || !user || hasFetched.current) return;
    hasFetched.current = true;
    fetchOrganization();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  const fetchOrganization = useCallback(async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organization`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          setFormData(prev => ({ ...prev, ...data }));
        }
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Organization profile saved!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save profile');
      }
    } catch (error) {
      console.error('Error saving organization:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'svg'].includes(ext)) {
      toast.error('Please upload a JPEG, PNG, or SVG file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        const token = localStorage.getItem('pitchpal_token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/logo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ image_data: base64, filename: file.name }),
        });
        if (res.ok) {
          const data = await res.json();
          handleChange('logo_url', `${process.env.REACT_APP_BACKEND_URL}${data.url}`);
          toast.success('Logo uploaded successfully');
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Failed to upload logo');
        }
        setUploadingLogo(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload logo');
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAFAFA]" data-testid="organization-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-[#0A0A0A]">
                My Organization
              </h1>
              <p className="text-[#5C6B67]">
                Set up your nonprofit profile to personalize proposals and grant matching
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card className="bg-white border-[#E2E8E6]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Basic Information
              </CardTitle>
              <CardDescription>
                Tell us about your nonprofit organization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Organization Name *</Label>
                  <StableInput
                    value={formData.name}
                    onChange={handleChange} field="name"
                    placeholder="Helping Hands Foundation"
                    required
                    data-testid="org-name-input"
                  />
                </div>
                <div>
                  <Label>Year Founded</Label>
                  <StableInput
                    value={formData.founded_year}
                    onChange={handleChange} field="founded_year"
                    placeholder="2010"
                  />
                </div>
              </div>
              
              <div>
                <Label>Mission Statement *</Label>
                <StableTextarea
                  value={formData.mission}
                  onChange={handleChange} field="mission"
                  placeholder="Describe your organization's mission - what you do and why..."
                  rows={3}
                  required
                  data-testid="org-mission-input"
                />
              </div>
              
              <div>
                <Label>Vision Statement</Label>
                <StableTextarea
                  value={formData.vision}
                  onChange={handleChange} field="vision"
                  placeholder="Describe your long-term vision for impact..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    Location
                  </Label>
                  <StableInput
                    value={formData.location}
                    onChange={handleChange} field="location"
                    placeholder="New York, NY"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <Globe className="w-4 h-4" />
                    Website
                  </Label>
                  <StableInput
                    value={formData.website}
                    onChange={handleChange} field="website"
                    placeholder="https://www.yournonprofit.org"
                  />
                </div>
              </div>

              <div>
                <Label>Tax ID (EIN)</Label>
                <StableInput
                  value={formData.tax_id}
                  onChange={handleChange} field="tax_id"
                  placeholder="12-3456789"
                />
              </div>
            </CardContent>
          </Card>

          {/* Impact & Programs */}
          <Card className="bg-white border-[#E2E8E6]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Impact & Programs
              </CardTitle>
              <CardDescription>
                Share your impact metrics and program overview
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Impact Statistics</Label>
                <StableTextarea
                  value={formData.impact_stats}
                  onChange={handleChange} field="impact_stats"
                  placeholder="Share your key impact numbers, e.g., 'Served 10,000 families, provided 50,000 meals, 95% of program graduates employed within 6 months'"
                  rows={3}
                />
              </div>
              
              <div>
                <Label>Programs Overview</Label>
                <StableTextarea
                  value={formData.programs_overview}
                  onChange={handleChange} field="programs_overview"
                  placeholder="Brief overview of your main programs and services..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="bg-white border-[#E2E8E6]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Primary Contact
              </CardTitle>
              <CardDescription>
                Contact information for proposals and communications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    Contact Name
                  </Label>
                  <StableInput
                    value={formData.contact_name}
                    onChange={handleChange} field="contact_name"
                    placeholder="Jane Smith"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    Contact Email
                  </Label>
                  <StableInput
                    value={formData.contact_email}
                    onChange={handleChange} field="contact_email"
                    placeholder="jane@nonprofit.org"
                    type="email"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    Contact Phone
                  </Label>
                  <StableInput
                    value={formData.contact_phone}
                    onChange={handleChange} field="contact_phone"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Branding */}
          <Card className="bg-white border-[#E2E8E6]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5 text-primary" />
                Branding
              </CardTitle>
              <CardDescription>
                Your brand colors for generated documents
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Organization Logo</Label>
                <div className="mt-2">
                  {formData.logo_url ? (
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-lg border border-[#E2E8E6] overflow-hidden bg-white flex items-center justify-center">
                        <img src={formData.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm text-[#5C6B67] truncate max-w-[200px]">Logo uploaded</p>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => logoInputRef.current?.click()}
                            disabled={uploadingLogo}
                            data-testid="change-logo-btn"
                          >
                            {uploadingLogo ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                            Change
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleChange('logo_url', '')}
                            className="text-red-500 hover:bg-red-50"
                            data-testid="remove-logo-btn"
                          >
                            <X className="w-3 h-3 mr-1" /> Remove
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="w-full border-2 border-dashed border-stone-300 rounded-lg p-6 text-center hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer"
                      data-testid="upload-logo-btn"
                    >
                      {uploadingLogo ? (
                        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
                      ) : (
                        <Upload className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                      )}
                      <p className="text-sm font-medium text-stone-700">
                        {uploadingLogo ? 'Uploading...' : 'Click to upload logo'}
                      </p>
                      <p className="text-xs text-[#5C6B67] mt-1">JPEG, PNG, or SVG (max 5MB)</p>
                    </button>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.svg"
                    onChange={handleLogoUpload}
                    className="hidden"
                    data-testid="logo-file-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Primary Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.primary_color}
                      onChange={handleChange} field="primary_color"
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <StableInput
                      value={formData.primary_color}
                      onChange={handleChange} field="primary_color"
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Secondary Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.secondary_color}
                      onChange={handleChange} field="secondary_color"
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <StableInput
                      value={formData.secondary_color}
                      onChange={handleChange} field="secondary_color"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button 
              type="submit" 
              className="bg-primary hover:bg-primary-800 text-white"
              disabled={saving}
              data-testid="save-org-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Organization Profile
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
